import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Card, Label, TextInput, Textarea } from "flowbite-react";
import { useForm, Controller } from "react-hook-form";
import { useGetData } from "../../../../../hooks/useGetData";
import SkyClear from "../../../../../img/SkyClear.png";
import { EvaluadoInterface } from "../../../../../interfaces/EvaluadoInterface";
import { FechasInterface } from "../../../../../interfaces/FechasInterface";
import { ReferenciasPersonalesInterface } from "../../../../../interfaces/ReferenciasPersonalesInterface";
import { NotasRPInterface } from "../../../../../interfaces/NotasRPInterface";
import { Loading } from "../../../../../components/Loading/Loading";
import Header from "../../../../../components/HeaderForms/HeaderForms";
import { PersonalesListaPrint } from "./PersonalesListaPrint";
import { ThemeContext } from "../../../../../components/ThemeContext/ThemeContext";

export const PersonalesListaScreen = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const tituloFormulario = "REFERENCIAS PERSONALES";
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [referenciasLaborales, setReferenciasLaborales] = useState([
    {
      name: "",
      address: "",
      phone: "",
      occupation: "",
      meet: "",
      comments: "",
      know: "",
      notes: "",
    },
  ]);
  const { evaluadosList_id } = useParams<{ evaluadosList_id: string }>();
  const [fechaNacimiento, setFechaNacimiento] = useState<string>("");
  const [edad, setEdad] = useState<number | null>(null);
  const { control, handleSubmit, setValue, reset } = useForm();
  const [formData, setFormData] = useState({
    visitDate: "",
    name: "",
  });

  const calcularEdad = (fechaNacimiento: string) => {
    const fechaNac = new Date(fechaNacimiento);
    const hoy = new Date();
    let edadCalculada = hoy.getFullYear() - fechaNac.getFullYear();
    const mes = hoy.getMonth() - fechaNac.getMonth();

    if (mes < 0 || (mes === 0 && hoy.getDate() < fechaNac.getDate())) {
      edadCalculada--;
    }

    return edadCalculada;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReferenciaChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setReferenciasLaborales((prev) =>
      prev.map((referencia, i) =>
        i === index ? { ...referencia, [name]: value } : referencia
      )
    );
  };

  const handleFechaNacimientoChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nuevaFechaNacimiento = event.target.value;
    setFechaNacimiento(nuevaFechaNacimiento);

    const nuevaEdad = calcularEdad(nuevaFechaNacimiento);
    setEdad(nuevaEdad);
  };

  const handlePrint = () => { 
    const printContent = document.getElementById("printableContent");

    if (printContent) {
      const printWindow = window.open("", "", "width=800,height=600");

      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Vista Previa de Impresión</title>
              <style>
                @media print {
                  body {
                    margin: 20px;
                    font-family: Arial, sans-serif;
                  }
                }
              </style>
            </head>
            <body>${printContent.innerHTML}</body>
          </html>
        `);

        const styles = document.styleSheets;
        for (let i = 0; i < styles.length; i++) {
          try {
            if (styles[i].cssRules) {
              const styleTag = printWindow.document.createElement("style");
              for (let j = 0; j < styles[i].cssRules.length; j++) {
                styleTag.appendChild(
                  printWindow.document.createTextNode(
                    styles[i].cssRules[j].cssText
                  )
                );
              }
              printWindow.document.head.appendChild(styleTag);
            }
          } catch (e) {
            console.warn("No se pudo copiar una hoja de estilo:", e);
          }
        }

        printWindow.document.close();
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            printWindow.close();
          }, 500);
        };
      } else {
        alert("No se pudo abrir la ventana de impresión.");
      }
    }
  };

  const renderHeader = () => (
    <>
      {/* Header */}
      <Header titulo={tituloFormulario} />
    </>
  );

  const queryEvaluado = useGetData<EvaluadoInterface>({
    url: `/evaluado/${evaluadosList_id}`,
    name: ["evaluado"],
  });

  const queryFecha = useGetData<FechasInterface[]>({
    url: `/evaluado/${evaluadosList_id}/fecha-visita-bienesinmuebles`,
    name: ["fecha-visita-bienesinmuebles"],
  });

  const queryPersonales = useGetData<ReferenciasPersonalesInterface>({
    url: `/referencia_personal/${evaluadosList_id}`,
    name: ["evaluado", `${evaluadosList_id}`, "referencia_personal"],
  });

  const queryNotasRP = useGetData<NotasRPInterface[]>({
    url: `/notas_rp/${evaluadosList_id}`,
    name: ["evaluado", `${evaluadosList_id}`, "notas_rp"],
  });

  const generoMap: { [key: string]: string } = {
    M: "Masculino",
    F: "Femenino",
    O: "Otro",
  };

  const generoTexto =
    generoMap[queryEvaluado.data?.genero as string] || "No especificado";

  useEffect(() => {
    if (queryEvaluado.data) {
      const fechaNac = queryEvaluado.data.fecha_nacimiento || "";
      setFechaNacimiento(fechaNac);

      const nuevaEdad = calcularEdad(fechaNac);
      setEdad(nuevaEdad);

      reset({
        nombre: queryEvaluado.data.nombre || "",
        apellido_paterno: queryEvaluado.data.apellido_paterno || "",
        apellido_materno: queryEvaluado.data.apellido_materno || "",
        rfc: queryEvaluado.data.rfc || "",
        genero: queryEvaluado.data.genero || "",
        estado_civil: queryEvaluado.data.estado_civil || "",
        escolaridad: queryEvaluado.data.escolaridad || "",
        fecha_nacimiento: fechaNac,
        curp: queryEvaluado.data.curp || "",
        empresa: queryEvaluado.data.empresa?.nombre || "",
        puesto: queryEvaluado.data.puesto || "",
        fecha_ingreso: queryEvaluado.data.fecha_ingreso || "",
      });
    }
  }, [queryEvaluado.data, reset]);

  useEffect(() => {
    if (queryPersonales.data) {
      const rp = queryPersonales.data;
      reset({
        nombre_rp1: rp.nombre_rp1 || "",
        domicilio_rp1: rp.domicilio_rp1 || "",
        telefono_rp1: rp.telefono_rp1 || "",
        ocupacion_rp1: rp.ocupacion_rp1 || "",
        anios_conocerlo_rp1: rp.anios_conocerlo_rp1 || "",
        comentarios_rp1: rp.comentarios_rp1 || "",
        como_conoce_rp1: rp.como_conoce_rp1 || "",
        nombre_rp2: rp.nombre_rp2 || "",
        domicilio_rp2: rp.domicilio_rp2 || "",
        telefono_rp2: rp.telefono_rp2 || "",
        ocupacion_rp2: rp.ocupacion_rp2 || "",
        anios_conocerlo_rp2: rp.anios_conocerlo_rp2 || "",
        comentarios_rp2: rp.comentarios_rp2 || "",
        como_conoce_rp2: rp.como_conoce_rp2 || "",
      });
    }
  }, [queryPersonales.data, reset]);

  useEffect(() => {
    if (queryNotasRP.data && queryNotasRP.data.length > 0) {
      const registroValido = queryNotasRP.data.find((item) => item.nota_rp);

      if (registroValido) {
        setValue("nota_rp", registroValido.nota_rp);
      }
    }
  }, [queryNotasRP.data, setValue]);

  if (queryEvaluado.isLoading) {
    return <Loading />;
  }

  const renderPage1 = () => (
    <div className={`space-y-4 p-4 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
      {/* Fecha Visita */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label
            htmlFor="fecha_refp"
            value="FECHA VISITA:"
            className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
          />
          <Controller
            name="fecha_refp"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextInput
                id="fecha_refp"
                name="fecha_refp"
                type="date"
                value={
                  queryFecha.data && queryFecha.data.length > 0
                    ? queryFecha.data[0].fecha_refl || ""
                    : ""
                }
                disabled={true}
                className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
              />
            )}
          />
        </div>
        {/* Campo para nombre */}
        <div className="col-span-2">
          <Label
            htmlFor="name"
            value="NOMBRE:"
            className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
          />
          <TextInput
            id="name"
            name="name"
            value={`${queryEvaluado.data?.nombre || ""} ${
              queryEvaluado.data?.apellido_paterno || ""
            } ${queryEvaluado.data?.apellido_materno || ""}`}
            disabled={true}
            onChange={handleInputChange}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
      </div>
      {/* Campo para RFC */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label htmlFor="rfc" value="RFC:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
          <TextInput
            id="rfc"
            name="rfc"
            value={queryEvaluado.data?.rfc || ""}
            disabled={true}
            onChange={handleInputChange}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* Edad */}
        <div className="col-span-1">
          <Label htmlFor="edad">EDAD:</Label>
          <TextInput
            id="edad"
            name="edad"
            value={edad !== null ? edad.toString() : ""}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* Género */}
        <div>
          <Label htmlFor="sex" value="SEXO:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
          <TextInput
            id="sex"
            name="sex"
            value={generoTexto}
            disabled={true}
            onChange={handleInputChange}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* Estado Civil */}
        <div>
          <Label htmlFor="civilStatus" value="ESTADO CIVIL:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
          <TextInput
            id="civilStatus"
            name="civilStatus"
            value={queryEvaluado.data?.estado_civil || ""}
            disabled={true}
            onChange={handleInputChange}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* Escolaridad */}
        <div>
          <Label htmlFor="education" value="ESCOLARIDAD:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
          <TextInput
            id="education"
            name="education"
            value={queryEvaluado.data?.escolaridad || ""}
            disabled={true}
            onChange={handleInputChange}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* Fecha de Nacimiento */}
        <div>
          <Label htmlFor="birthDate" value="FECHA DE NACIMIENTO:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
          <TextInput
            id="birthDate"
            name="birthDate"
            type="date"
            value={queryEvaluado.data?.fecha_nacimiento || ""}
            disabled={true}
            onChange={handleInputChange}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* CURP */}
        <div>
          <Label htmlFor="curp" value="CURP:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
          <TextInput
            id="curp"
            name="curp"
            value={queryEvaluado.data?.curp || ""}
            disabled={true}
            onChange={handleInputChange}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* Empresa */}
        <div className="col-span-1">
          <Label htmlFor="empresa">EMPRESA:</Label>
          <TextInput
            id="empresa_id"
            name="empresa"
            value={queryEvaluado.data?.empresa_id?.nombre || ""}
            onChange={handleInputChange}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
      </div>
    </div>
  );

  const renderPage2 = (index: number) => (
    <div className={`space-y-4 p-4 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
      {[...Array(3)].map((_, index) => (
        <div key={index}>
          <h2 className={`text-xl font-bold mb-4 p-2 rounded-t ${isDarkMode ? "bg-blue-800 text-white" : "bg-customBlue text-white"}`}>
            REFERENCIA PERSONAL {index + 1}{" "}
          </h2>
          <div className="space-y-4">
            <div className="flex flex-col text-left">
              <Label
                htmlFor={`nombre_rp${index + 1}`}
                value="NOMBRE:"
                className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
              />
              <Controller
                name={`nombre_rp${index + 1}`}
                control={control}
                defaultValue={
                  queryPersonales.data
                    ? queryPersonales.data[
                        `nombre_rp${
                          index + 1
                        }` as keyof ReferenciasPersonalesInterface
                      ] || ""
                    : ""
                }
                render={({ field }) => (
                  <input
                    type="text"
                    id={`empresa_rl${index + 1}`}
                    {...field}
                    disabled={true}
                    onChange={(e) => field.onChange(e.target.value)}
                    className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 border-gray-300"}`}
                  />
                )}
              />
            </div>
            <div className="flex flex-col text-left">
              <Label
                htmlFor="domicilio_rp"
                value="DOMICILIO:"
                className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
              />
              <Controller
                name={`domicilio_rp${index + 1}`}
                control={control}
                defaultValue={
                  queryPersonales.data
                    ? queryPersonales.data[
                        `domicilio_rp${
                          index + 1
                        }` as keyof ReferenciasPersonalesInterface
                      ] || ""
                    : ""
                }
                render={({ field }) => (
                  <input
                    type="text"
                    id={`domicilio_rp${index + 1}`}
                    {...field}
                    disabled={true}
                    onChange={(e) => field.onChange(e.target.value)}
                    className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 border-gray-300"}`}
                  />
                )}
              />
            </div>
            <div className="flex flex-col text-left">
              <Label
                htmlFor={`telefono_rp${index + 1}`}
                value="TELÉFONO:"
                className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
              />
              <Controller
                name={`telefono_rp${index + 1}`}
                control={control}
                defaultValue={
                  queryPersonales.data
                    ? queryPersonales.data[
                        `telefono_rp${
                          index + 1
                        }` as keyof ReferenciasPersonalesInterface
                      ] || ""
                    : ""
                }
                render={({ field }) => (
                  <input
                    type="text"
                    id={`telefono_rp${index + 1}`}
                    {...field}
                    disabled={true}
                    onChange={(e) => field.onChange(e.target.value)}
                    className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 border-gray-300"}`}
                  />
                )}
              />
            </div>
            <div className="flex flex-col text-left">
              <Label
                htmlFor={`ocupacion_rp${index + 1}`}
                value="OCUPACIÓN:"
                className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
              />
              <Controller
                name={`ocupacion_rp${index + 1}`}
                control={control}
                defaultValue={
                  queryPersonales.data
                    ? queryPersonales.data[
                        `ocupacion_rp${
                          index + 1
                        }` as keyof ReferenciasPersonalesInterface
                      ] || ""
                    : ""
                }
                render={({ field }) => (
                  <input
                    type="text"
                    id={`ocupacion_rp${index + 1}`}
                    {...field}
                    disabled={true}
                    onChange={(e) => field.onChange(e.target.value)}
                    className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 border-gray-300"}`}
                  />
                )}
              />
            </div>
            <div className="flex flex-col text-left">
              <Label
                htmlFor={`anios_conocerlo_rp${index + 1}`}
                value="AÑOS DE CONOCERLO:"
                className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
              />
              <Controller
                name={`anios_conocerlo_rp${index + 1}`}
                control={control}
                defaultValue={
                  queryPersonales.data
                    ? queryPersonales.data[
                        `anios_conocerlo_rp${
                          index + 1
                        }` as keyof ReferenciasPersonalesInterface
                      ] || ""
                    : ""
                }
                render={({ field }) => (
                  <input
                    type="text"
                    id={`anios_conocerlo_rp${index + 1}`}
                    {...field}
                    disabled={true}
                    onChange={(e) => field.onChange(e.target.value)}
                    className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 border-gray-300"}`}
                  />
                )}
              />
            </div>
            <div className="flex flex-col text-left">
              <Label
                htmlFor={`comentarios_rp${index + 1}`}
                value="COMENTARIOS:"
                className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
              />
              <Controller
                name={`comentarios_rp${index + 1}`}
                control={control}
                defaultValue={
                  queryPersonales.data
                    ? queryPersonales.data[
                        `comentarios_rp${
                          index + 1
                        }` as keyof ReferenciasPersonalesInterface
                      ] || ""
                    : ""
                }
                render={({ field }) => (
                  <input
                    type="text"
                    id={`comentarios_rp${index + 1}`}
                    {...field}
                    disabled={true}
                    onChange={(e) => field.onChange(e.target.value)}
                    className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 border-gray-300"}`}
                  />
                )}
              />
            </div>
            <div className="flex flex-col text-left">
              <Label
                htmlFor={`como_conoce_rp${index + 1}`}
                value="LO RECOMIENDA:"
                className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
              />
              <Controller
                name={`como_conoce_rp${index + 1}`}
                control={control}
                defaultValue={
                  queryPersonales.data
                    ? queryPersonales.data[
                        `como_conoce_rp${
                          index + 1
                        }` as keyof ReferenciasPersonalesInterface
                      ] || ""
                    : ""
                }
                render={({ field }) => (
                  <input
                    type="text"
                    id={`como_conoce_rp${index + 1}`}
                    {...field}
                    disabled={true}
                    onChange={(e) => field.onChange(e.target.value)}
                    className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 border-gray-300"}`}
                  />
                )}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const addReferenciaLaboral = () => {
    setReferenciasLaborales([
      ...referenciasLaborales,
      {
        name: "",
        address: "",
        phone: "",
        occupation: "",
        meet: "",
        comments: "",
        know: "",
        notes: "",
      },
    ]);
  };

  const validateAndRemoveEmptyReferences = () => {
    setReferenciasLaborales((prev) =>
      prev.filter((referencia, index) => {
        if (index === 0) return true;
        const isFilled = Object.values(referencia).some(
          (value) => value.trim() !== ""
        );
        return isFilled;
      })
    );
  };

  const handlePageChange = (direction: "next" | "prev") => {
    validateAndRemoveEmptyReferences();
    if (direction === "next") {
      setCurrentPage((prev) => prev + 1);
    } else if (direction === "prev") {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
      <div className={`container mx-auto p-4`}>
        <form className={`max-w-4xl mx-auto rounded-lg shadow-lg overflow-hidden ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
          {renderHeader()}
          {currentPage === 1 && renderPage1()}
          {currentPage === 2 && (
            <div>
              {referenciasLaborales.map((_, index) => (
                <div key={index} className={`referencia-${index}`}>
                  {renderPage2(index)}
                </div>
              ))}
              <h2 className={`text-xl font-bold mb-4 p-2 rounded-t ${isDarkMode ? "bg-blue-800 text-white" : "bg-customBlue text-white"}`}>
                NOTAS
              </h2>
              <div className={`space-y-4 m-4 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
                <Controller
                  name="nota_rp"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Textarea
                      id="nota_rp"
                      {...field}
                      name="nota_rp"
                      rows={6}
                      onInput={(e) => {
                        const target = e.target as HTMLTextAreaElement;
                        target.style.height = "auto";
                        target.style.height = `${target.scrollHeight}px`;
                      }}
                      disabled={true}
                      className={`mt-2 p-2 border rounded-md resize-none ${isDarkMode ? "bg-gray-700 border-gray-600 text-gray-300" : "bg-gray-100 border-gray-300"}`}
                      style={{ height: "auto", overflow: "hidden" }}
                    />
                  )}
                />
              </div>
              <div className={`m-4 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                <p className="text-left">
                  {" "}
                  Bajo protesta de decir verdad, hago constar que los datos
                  proporcionados son verdaderos.{" "}
                </p>
                <p className="text-left">
                  Asimismo, autorizo a la empresa SKA TRACKING AND SECURITY S.A.
                  de C.V. para que proporcione toda la Información, documentos y
                  todos los datos que derive de las evaluaciones de pruebas de
                  confianza que se me realicen.{" "}
                </p>
              </div>
            </div>
          )}
          {/* Botones de navegación */}
          <div className="mt-6 p-4 flex justify-between">
            {currentPage < 2 && (
              <Button
                color="red"
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate(`/domiciliaria/${evaluadosList_id}`);
                }}
                className={`${isDarkMode ? "bg-red-600 hover:bg-red-700 text-white" : "bg-red-500 text-white"}`}
              >
                Cancelar
              </Button>
            )}
            {currentPage > 1 && (
              <Button
                color="blue"
                onClick={() => {
                  window.scrollTo(0, 0);
                  handlePageChange("prev");
                }}
                className={`${isDarkMode ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-blue-500 text-white"}`}
              >
                Anterior
              </Button>
            )}
            {currentPage < 2 ? (
              <Button
                color="blue"
                onClick={() => {
                  window.scrollTo(0, 0);
                  handlePageChange("next");
                }}
                className={`${isDarkMode ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-blue-500 text-white"}`}
              >
                Siguiente
              </Button>
            ) : (
              <div className="flex space-x-4">
                <Button
                  color="blue"
                  onClick={handlePrint}
                  className={`${isDarkMode ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-blue-500 text-white"}`}
                >
                  Imprimir
                </Button>
                <button
                  type="button"
                  className={`rounded px-4 py-2 ${isDarkMode ? "bg-red-600 hover:bg-red-700 text-white" : "bg-red-500 text-white"}`}
                  onClick={() => {
                    window.scrollTo(0, 0);
                    navigate(`/domiciliaria/${evaluadosList_id}`);
                  }}
                >
                  Salir
                </button>
              </div>
            )}
          </div>
        </form>
        {/* Imprimir */}
        <div id="printableContent" style={{ display: "none" }}>
          <PersonalesListaPrint />
        </div>
      </div>

    </div>
  );
};
